'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore, type EditorMedia } from '@/lib/editor/editorStore';

/**
 * `@`-mention library picker for the editor chat composer (Canny task 3).
 *
 * Mounted by the composer whenever the user is mid-typing a `@query`; reads
 * the shared media library straight from the store, filters by `query`
 * (case-insensitive substring of `title`), and owns its own keyboard
 * handling (↑/↓/Enter/Esc) via a `window` keydown listener so the composer
 * doesn't need to route key events through it. Selecting or dismissing calls
 * back into the parent (`onPick`/`onClose`); the parent is responsible for
 * unmounting the popup (it renders only while a mention query is active).
 */
export interface MentionPopupProps {
  query: string;
  onPick: (media: EditorMedia) => void;
  onClose: () => void;
}

function formatMentionDuration(seconds: number | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function MentionPopup({ query, onPick, onClose }: MentionPopupProps) {
  const library = useEditorStore((s) => s.library);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return library;
    return library.filter((item) => item.title.toLowerCase().includes(q));
  }, [library, query]);

  // Keep the active row in range whenever the filtered list changes shape
  // (e.g. the query narrows the list while row 4 was active).
  useEffect(() => {
    setActiveIndex((prev) => {
      if (filtered.length === 0) return 0;
      return Math.min(prev, filtered.length - 1);
    });
  }, [filtered]);

  // Keep the active row scrolled into view as the user arrows through the list.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (filtered.length === 0) return;
        setActiveIndex((prev) => (prev + 1) % filtered.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (filtered.length === 0) return;
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
      } else if (event.key === 'Enter') {
        // Always suppress the newline while the popup is open (even on an
        // empty list) so Enter is a clean pick / no-op, never a stray newline.
        event.preventDefault();
        if (filtered.length === 0) return;
        const picked = filtered[activeIndex];
        if (picked) onPick(picked);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filtered, activeIndex, onPick, onClose]);

  return (
    <div className="absolute bottom-full left-0 mb-2 w-full max-h-64 overflow-y-auto rounded-xl border border-white/10 bg-[#160c26] p-1 shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
      <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-black uppercase tracking-wider text-white/40">
        Медиа из библиотеки
      </p>
      {filtered.length === 0 ? (
        <p className="px-2.5 py-3 text-center text-xs text-white/50">Ничего не найдено</p>
      ) : (
        <div className="space-y-0.5">
          {filtered.map((item, index) => {
            const duration = formatMentionDuration(item.duration);
            const isActive = index === activeIndex;
            return (
              <button
                key={item.id}
                ref={isActive ? activeRef : undefined}
                type="button"
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onPick(item)}
                className={`flex w-full items-center gap-2 rounded-lg p-1.5 text-left transition ${
                  isActive ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
              >
                <div className="relative h-10 w-14 shrink-0 overflow-hidden rounded-md bg-black">
                  <video
                    src={`${item.src}#t=0.5`}
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-white">{item.title}</p>
                  {duration && <p className="text-[11px] text-white/45">{duration}</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
